import React, { CSSProperties, FC } from "react"
import { graphql } from "gatsby"

import { StatBlockFragmentFragment } from "@graphqlTypes"
import { convertStringToKebabCase } from "@utils"

import LpSection from "@components/lp/LpSection/LpSection"

import StatBlockItem from "./components/StatBlockItem/StatBlockItem"

import "./StatBlock.scss"

export enum STAT_BLOCK_THEME_COLORS {
  COBALT = "COBALT",
  PURPLE = "PURPLE",
  MAGENTA = "MAGENTA",
  GREEN = "GREEN",
  SLATE = "SLATE",
  CLAY = "CLAY",
}

const getThemeColors = (themeColor: STAT_BLOCK_THEME_COLORS): CSSProperties => {
  switch (themeColor) {
    case STAT_BLOCK_THEME_COLORS.COBALT:
      return {
        "--StatBlock--background": `var(--color--cobalt-900)`,
        "--StatBlock--title": "var(--color--purple-300)",
        "--StatBlock--vRuleAndIconContainer": `var(--color--cobalt-800)`,
        "--StatBlock--statIcon": "var(--color--purple-300)",
        "--StatBlock--bodyText": "rgba(var(--color--purple-300-rgb), 0.8)",
      }
    case STAT_BLOCK_THEME_COLORS.PURPLE:
      return {
        "--StatBlock--background": `var(--color--purple-900)`,
        "--StatBlock--title": "var(--color--red-300)",
        "--StatBlock--vRuleAndIconContainer": `var(--color--purple-800)`,
        "--StatBlock--statIcon": "var(--color--red-300)",
        "--StatBlock--bodyText": "rgba(var(--color--red-300-rgb), 0.8)",
      }
    case STAT_BLOCK_THEME_COLORS.MAGENTA:
      return {
        "--StatBlock--background": `var(--color--magenta-900)`,
        "--StatBlock--title": "var(--color--orange-300)",
        "--StatBlock--vRuleAndIconContainer": `var(--color--magenta-800)`,
        "--StatBlock--statIcon": "var(--color--orange-300)",
        "--StatBlock--bodyText": "rgba(var(--color--orange-300-rgb), 0.8)",
      }
    case STAT_BLOCK_THEME_COLORS.GREEN:
      return {
        "--StatBlock--background": "var(--color--slate-900)",
        "--StatBlock--title": `var(--color--green-300)`,
        "--StatBlock--vRuleAndIconContainer": "var(--color--slate-800)",
        "--StatBlock--statIcon": `var(--color--green-300)`,
        "--StatBlock--bodyText": `rgba(var(--color--green-300-rgb), 0.8)`,
      }
    case STAT_BLOCK_THEME_COLORS.SLATE:
      return {
        "--StatBlock--background": `var(--color--slate-200)`,
        "--StatBlock--title": "var(--color--text-headline)",
        "--StatBlock--vRuleAndIconContainer": `var(--color--slate-300)`,
        "--StatBlock--statIcon": "var(--color--text-headline)",
        "--StatBlock--bodyText": "var(--color--text-default)",
      }
    case STAT_BLOCK_THEME_COLORS.CLAY:
    default:
      return {
        "--StatBlock--background": `var(--color--clay-100)`,
        "--StatBlock--title": "var(--color--text-headline)",
        "--StatBlock--vRuleAndIconContainer": `var(--color--clay-300)`,
        "--StatBlock--statIcon": "var(--color--text-headline)",
        "--StatBlock--bodyText": "var(--color--text-default)",
      }
  }
}

const StatBlock: FC<StatBlockFragmentFragment> = ({
  background,
  contentful_id,
  themeColor,
  hasNegativeTopMargin,
  marginBottom,
  title,
  items,
}) => {
  const theme = getThemeColors(themeColor as STAT_BLOCK_THEME_COLORS || STAT_BLOCK_THEME_COLORS.CLAY)
  return (
    <LpSection
      background={background || `var(--StatBlock--background)`}
      className="StatBlock"
      marginBottom={marginBottom}
      style={theme}
      uniqueId={contentful_id}
      useNegativeTopMargin={hasNegativeTopMargin}
    >
      <div className="container">
        {title && (
          <h3
            className="type--text-M font-family--mono"
            id={convertStringToKebabCase(title)}
          >
            {title}
          </h3>
        )}
        <div className="flex">
          {items && items.map((item) => !item ? null : (
            <StatBlockItem key={item.id} {...item} theme={theme} />
          ))}
        </div>
      </div>
    </LpSection>
  )
}

export const StatBlockFragment = graphql`
  fragment StatBlockItemFragment on ContentfulLpStatBlockItem {
    icon
    id
    number
    unit
    stat
    description {
      raw
    }
  }

  fragment StatBlockFragment on ContentfulLpStatBlock {
    contentful_id
    background
    hasNegativeTopMargin
    id
    items {
      ...StatBlockItemFragment
    }
    marginBottom
    themeColor
    title
  }
`

export default StatBlock
